import { useEffect, useMemo, useRef } from 'react';
import { Grid } from '@mui/material';
import { useForm } from 'react-hook-form';
import { PreviousNextButtons } from '@/components/forms/other/PreviousNextButtons/PreviousNextButtons';
import { DefaultPageProps } from '@/types/types/page/pages';
import {
    IAllRegistrationData,
    IRegistrationProfessionalDetails,
} from '@/types/types/forms/registration/registration.interface';
import { IWalletPersonalData } from '@/types/types/forms/wallet/wallet.interface';
import { useStatesQuery } from '@/hooks/queries/dictionary/dictionaryQueries';
import { useDynamicQuestions } from '@/hooks/forms/register/useDynamicQuestions';
import { useProfessionalDetails } from '@/hooks/forms/register/useProfessionalDetails';
import { useProfessionalDetailsQuestionsQuery } from '@/hooks/queries/registration/professionalDetailsQueries';
import { registerService } from '@/services/api/registerService/registerService';
import { SelectField, TextField, CheckboxField } from '@/components/forms/inputs';
import {
    PHYSICIAN_PROFESSION_GROUP,
    PROFESSIONS,
    USA_MEDICAL_NUMBER_OPTIONAL_GROUP,
    USA_MEDICAL_NUMBER_REQUIRED_GROUP,
} from '@/consts/forms/registration';
import { useServerErrors } from '@/hooks/forms/useServerErrors';
import { createSelectOptionsFromArray } from '@/utils/forms/selectOptions';
import { usePhrasesStore } from '@/store/phrasesStore';
import { useRegistrationStore } from '@/store/pages/registrationStore/registrationStore';
import { useRouter } from '@/hooks/general/useRouter/useRouter';
import { PROFESSIONAL_DETAILS_PHRASES } from '@/consts/phrases/registrationPhrases';
import { registerFormModel } from '../registerFormModel';
import { RegistrationStep } from '@/types/enums/forms/registration/registrationWizard';
import { useCurrentPath } from '@/hooks/general/useCurrentPath/useCurrentPath';
import { useLocale } from '@/hooks/general/useLocale/useLocale';
import { FormGridErrors } from '@/components/forms/alerts/FormGridErrors/FormGridErrors';
import { getAccountLocale } from '@/utils/locale/getAccountLocale';
import { addLocaleToUrl } from '@/utils/locale/addLocaleToUrl';
import { countries } from '@/consts/languageAccordance';
import { isFeatureEnabled } from '@/config';
import * as Sentry from '@sentry/nextjs';

const COUNTRIES_WITH_STATES = ['USA', 'DEU', 'AUT'];
const COUNTRIES_THAT_DO_NOT_REQUIRE_MEDICAL_SCHOOL = [
    'SWE',
    'DNK',
    'FIN',
    'NOR',
    'BEL',
    'NLD',
    'AUT',
    'CHE',
    'GBR',
    'DEU',
    'FRA',
    'ITA',
    'ESP',
];

interface ProfessionalDetailsFormProps {
    registrationData?: IAllRegistrationData;
    nonMemberPersonalData?: Partial<IWalletPersonalData>;
    dictionary: DefaultPageProps['dictionary'];
    professionChanged: boolean;
    revalidateRegistrationData: () => Promise<void>;
}

export const RegistrationProfessionalDetailsForm = ({
    registrationData,
    nonMemberPersonalData,
    dictionary,
    professionChanged,
    revalidateRegistrationData,
}: ProfessionalDetailsFormProps) => {
    const errorRef = useRef<HTMLDivElement | null>(null);
    const router = useRouter();
    const { pathname, asPath } = useCurrentPath();
    const i18nLocale = useLocale();
    const { setLocale } = usePhrasesStore();
    const hasAutoLocaleSwitchingDisabled = isFeatureEnabled('localeAutoSwitchingDisabled');
    const useNewMedicalNumberLogic = isFeatureEnabled('newMedicalNumber');

    const { goToNextStep, goToPrevStep, registrationId } = useRegistrationStore((state) => ({
        goToNextStep: state.goToNextStep,
        goToPrevStep: state.goToPrevStep,
        registrationId: state.registrationId,
    }));

    const accountLocale = getAccountLocale(
        registrationData?.preferredLanguageId,
        registrationData?.countryCode,
        dictionary?.countries,
    );

    useEffect(() => {
        if (hasAutoLocaleSwitchingDisabled) {
            return;
        }
        try {
            if (accountLocale && accountLocale !== i18nLocale.locale) {
                setLocale(accountLocale);
                router.push(addLocaleToUrl(accountLocale, pathname, asPath));
                // Refresh to fetch new content based on the updated locale
                router.refresh();
            }
        } catch (e) {
            Sentry.captureException(e);
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [accountLocale]);

    const { getDefaultValues, specialtiesList, yearsList } = useProfessionalDetails({
        dictionary,
        registrationData,
        nonMemberPersonalData,
        restrictCountries: true,
    });

    const statesList = useStatesQuery(
        registrationData?.countryCode || nonMemberPersonalData?.countryCode,
    );
    const hasStates = statesList && statesList.length > 0;

    const professionalDetailsQuestions = useProfessionalDetailsQuestionsQuery(
        registrationData?.professionId,
    );
    const { control, handleSubmit, formState, getValues, setError, clearErrors, watch } = useForm<
        Partial<IRegistrationProfessionalDetails>
    >({
        mode: 'onChange',
        defaultValues: professionChanged ? undefined : getDefaultValues,
    });

    const { handleServerErrors, serverErrors } = useServerErrors({
        setError,
        clearErrors,
        getValues,
    });

    const { errors, isSubmitting } = formState;
    const submitData = async (values: Partial<IRegistrationProfessionalDetails>) => {
        try {
            if (!registrationId) throw new Error('No registration id provided');
            await registerService.putProfessionalDetails(registrationId, values);
            goToNextStep();
            await revalidateRegistrationData();
        } catch (err) {
            handleServerErrors(err);
        }
    };
    const hasProfession = (professionsToCheck: number[]) =>
        registrationData?.professionId &&
        professionsToCheck.includes(registrationData.professionId);

    const medicalNumberPhraseId = useMemo(
        () =>
            dictionary?.countries.find(
                (country) =>
                    country.countryCode === registrationData?.countryCode ||
                    nonMemberPersonalData?.countryCode,
            )?.medicalNumberPhraseId,
        [dictionary, registrationData?.countryCode, nonMemberPersonalData?.countryCode],
    );

    const DynamicQuestions = useDynamicQuestions(control, professionalDetailsQuestions, errors, {
        required: true,
    });

    const getSelectedCountryCode = () =>
        registrationData?.countryCode || nonMemberPersonalData?.countryCode;

    const isCountryThatIncludes = (countryCodes: string[]) => (countryCode: string | undefined) =>
        countryCode && countryCodes.includes(countryCode);
    const isUSA = isCountryThatIncludes([countries.us.code3]);
    const isIndia = isCountryThatIncludes([countries.in.code3]);

    const isCountryWithStates = isCountryThatIncludes(COUNTRIES_WITH_STATES);
    const isCountryThatDoesNotRequireMedicalSchool = isCountryThatIncludes(
        COUNTRIES_THAT_DO_NOT_REQUIRE_MEDICAL_SCHOOL,
    );

    const selectedCountryIsUSA = isUSA(getSelectedCountryCode());
    const selectedCountryIsIndia = isIndia(getSelectedCountryCode());

    const selectedCountryDoesNotRequireMedicalSchool =
        isCountryThatDoesNotRequireMedicalSchool(getSelectedCountryCode());
    const selectedCountryHasStates = isCountryWithStates(getSelectedCountryCode());

    const {
        specialtyId,
        medicalNumber,
        medicalNumberNPI,
        stateMedicalCouncil,
        medicalNumberOptOut,
        graduationYear,
        medicalSchool,
        stateCodeLicensed,
        primaryWorkplace,
        primaryWorkplacePhone,
        professionalIdNumber,
    } = registerFormModel[RegistrationStep.ProfessionalDetails];

    const isMedicalNumberOptedOut = watch('medicalNumberOptOut');

    useEffect(() => {
        if (isMedicalNumberOptedOut) {
            clearErrors(['medicalNumber', 'stateMedicalCouncil']);
        }
    }, [isMedicalNumberOptedOut, clearErrors]);

    const hasMedicalNumberWithOptOut =
        useNewMedicalNumberLogic && selectedCountryIsUSA
            ? hasProfession(USA_MEDICAL_NUMBER_REQUIRED_GROUP)
            : hasProfession(PHYSICIAN_PROFESSION_GROUP);

    const hasOptionalMedicalNumber =
        useNewMedicalNumberLogic &&
        selectedCountryIsUSA &&
        hasProfession(USA_MEDICAL_NUMBER_OPTIONAL_GROUP);

    return (
        <form onSubmit={handleSubmit(submitData)} noValidate>
            <Grid container spacing={2}>
                <FormGridErrors
                    errors={serverErrors}
                    gridSx={{ pb: 4 }}
                    generalErrorId="message_box"
                    generalErrorRef={errorRef}
                />
                {hasProfession([
                    ...PHYSICIAN_PROFESSION_GROUP,
                    PROFESSIONS.PHYSICIAN_ASSISTANT,
                ]) && (
                    <Grid size={12}>
                        <SelectField
                            control={control}
                            name={specialtyId.name}
                            label={specialtyId.label}
                            data={specialtiesList}
                            data-testid="selectYourSpecialty-input"
                            error={errors.specialtyId}
                            fullWidth
                            required
                        />
                    </Grid>
                )}
                {hasMedicalNumberWithOptOut && (
                    <>
                        {!useNewMedicalNumberLogic && selectedCountryIsUSA && (
                            <Grid size={12}>
                                <TextField
                                    control={control}
                                    name={medicalNumberNPI.name}
                                    label={medicalNumberNPI.label}
                                    type="text"
                                    fullWidth
                                    error={errors.medicalNumberNPI}
                                    data-testid="npiNumber-input"
                                />
                            </Grid>
                        )}
                        <Grid size={12}>
                            <TextField
                                control={control}
                                name={medicalNumber.name}
                                label={medicalNumberPhraseId ?? medicalNumber.label}
                                type="text"
                                error={errors.medicalNumber}
                                data-testid="meNumber-input"
                                fullWidth
                                disabled={isMedicalNumberOptedOut}
                                required={!isMedicalNumberOptedOut}
                            />
                        </Grid>
                        {selectedCountryIsIndia && (
                            <TextField
                                control={control}
                                name={stateMedicalCouncil.name}
                                label={stateMedicalCouncil.label}
                                type="text"
                                error={errors.stateMedicalCouncil}
                                data-testid="stateMedicalCouncil-input"
                                fullWidth
                                disabled={isMedicalNumberOptedOut}
                                required={!isMedicalNumberOptedOut}
                            />
                        )}
                        <Grid
                            size={12}
                            sx={{
                                pl: 2,
                            }}
                        >
                            <CheckboxField
                                control={control}
                                name={medicalNumberOptOut.name}
                                label={medicalNumberOptOut.label}
                                labelReplacements={[
                                    medicalNumberPhraseId ??
                                        PROFESSIONAL_DETAILS_PHRASES.MEDICAL_NUMBER,
                                ]}
                                sx={{ mt: -2 }}
                            />
                        </Grid>
                    </>
                )}
                {hasOptionalMedicalNumber && (
                    <Grid size={12}>
                        <TextField
                            control={control}
                            name={medicalNumber.name}
                            label={medicalNumberPhraseId ?? medicalNumber.label}
                            type="text"
                            error={errors.medicalNumber}
                            data-testid="meNumber-input"
                            fullWidth
                        />
                    </Grid>
                )}
                {hasProfession([...PHYSICIAN_PROFESSION_GROUP]) && (
                    <>
                        <Grid size={12}>
                            <SelectField
                                control={control}
                                name={graduationYear.name}
                                label={graduationYear.label}
                                data={yearsList}
                                error={errors.graduationYear}
                                required
                                fullWidth
                            />
                        </Grid>
                        <Grid size={12}>
                            <TextField
                                control={control}
                                name={medicalSchool.name}
                                label={medicalSchool.label}
                                type="text"
                                role="textbox"
                                error={errors.medicalSchool}
                                data-testid="medicalSchoolWentTo-input"
                                fullWidth
                                required={!selectedCountryDoesNotRequireMedicalSchool}
                                slotProps={{
                                    htmlInput: {
                                        maxLength: 100,
                                    },
                                }}
                            />
                        </Grid>
                        {selectedCountryHasStates && hasStates && (
                            <Grid size={12}>
                                <SelectField
                                    control={control}
                                    name={stateCodeLicensed.name}
                                    label={stateCodeLicensed.label}
                                    data={createSelectOptionsFromArray({
                                        label: 'name',
                                        value: 'stateCode',
                                    })(statesList)}
                                    fullWidth
                                    error={errors.stateCodeLicensed}
                                    data-testid="country-input"
                                    required
                                />
                            </Grid>
                        )}
                    </>
                )}
                {!hasProfession([PROFESSIONS.PATIENT_CONSUMER]) && (
                    <>
                        <Grid size={12}>
                            <TextField
                                control={control}
                                name={primaryWorkplace.name}
                                label={primaryWorkplace.label}
                                type="text"
                                role="textbox"
                                fullWidth
                                error={errors.primaryWorkplace}
                                data-testid="primaryWorkplaceName-input"
                                required={!hasProfession([...PHYSICIAN_PROFESSION_GROUP])}
                            />
                        </Grid>
                        <Grid size={12}>
                            <TextField
                                control={control}
                                name={primaryWorkplacePhone.name}
                                label={primaryWorkplacePhone.label}
                                type="text"
                                role="textbox"
                                fullWidth
                                error={errors.primaryWorkplacePhone}
                                data-testid="workplaceMainlineNumber-input"
                            />
                        </Grid>
                        {!hasProfession([...PHYSICIAN_PROFESSION_GROUP]) && (
                            <Grid size={12}>
                                <TextField
                                    control={control}
                                    name={professionalIdNumber.name}
                                    label={professionalIdNumber.label}
                                    type="text"
                                    role="textbox"
                                    fullWidth
                                    error={errors.professionalIdNumber}
                                    data-testid="professionalIdNumber-input"
                                />
                            </Grid>
                        )}
                    </>
                )}
                {DynamicQuestions}
            </Grid>
            <Grid
                container
                sx={{
                    justifyContent: 'flex-end',
                    marginTop: 2,
                }}
            >
                <PreviousNextButtons
                    onPreviousClicked={goToPrevStep}
                    nextDisabled={isSubmitting}
                    previousDisabled={isSubmitting}
                />
            </Grid>
        </form>
    );
};
